import { PlusIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import React, { type FC, useState, useCallback, useEffect } from 'react';

import { TemplateAPIs, TemplateTagAPIs } from '@/api';
import { useTagQuery } from '@/api/queries/tag';
import type { Tag, TagWithCount, TemplateTag } from '@/api/types/tag';
import { CheckIcon } from '@/common/check-icon';
import { CreateTagModal } from '@/common/create-tag-modal';
import { Menu } from '@/common/menu';
import { UncheckIcon } from '@/common/uncheck-icon';


export interface TagsMenuProps {
    templateId: number;
    tags: TemplateTag[];
    setTags: (tags: TemplateTag[]) => void;
}

export const TagsMenu: FC<TagsMenuProps> = ({ templateId, tags, setTags }) => {
    const [tagList, setTagList] = useState<Tag[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    const { refetch: _ } = useTagQuery({
        onSuccess: (data: TagWithCount[]) => setTagList(data),
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    const getSortedTags = (data: Tag[]) => {
        const appliedTags = data.filter((tag) => tags.some((t) => t.tag.id === tag.id));
        const unAppliedTags = data.filter((tag) => !tags.some((t) => t.tag.id === tag.id));
        const sorted = [...appliedTags, ...unAppliedTags];
        return sorted;
    };

    const deleteTag = useCallback(
        async (tagId: number) => {
            try {
                await TemplateTagAPIs.deleteTemplateTag(tagId);
                const tags = await TemplateAPIs.getTemplateTags(templateId);
                setTags(tags);
            } catch (error) {
                console.error('Error deleting tag:', error);
            }
        },
        [templateId, setTags],
    );

    const addTag = useCallback(
        async (tagId: number) => {
            try {
                await TemplateTagAPIs.createTemplateTag(templateId, tagId);
                const tags = await TemplateAPIs.getTemplateTags(templateId);
                setTags(tags);
            } catch (error) {
                console.error('Error adding tag:', error);
            }
        },
        [templateId, setTags],
    );

    return (
        <>
            <Menu
                icon={
                    <div className="flex flex-row gap-2 items-center">
                        <p className="text-sm text-gray-110">Select Tags</p>
                        <ChevronDownIcon className="text-gray-110 h-4 w-4" />
                    </div>
                }
                items={[
                    ...getSortedTags(tagList).map((tag) => {
                        const templateTag = tags.find((t) => t.tag.id === tag.id);
                        return {
                            name: tag.name,
                            icon: templateTag ? <CheckIcon /> : <UncheckIcon />,
                            onClick: () => {
                                if (templateTag) {
                                    deleteTag(templateTag.id);
                                } else {
                                    addTag(tag.id);
                                }
                            },
                            id: tag.id.toString(),
                        };
                    }),
                    {
                        name: 'Add Tag',
                        icon: <PlusIcon className="h-4 w-4 text-blue-30" />,
                        color: 'blue-30',
                        onClick: () => setIsOpen(true),
                        id: 'Add Tag',
                    },
                ]}
            />

            <CreateTagModal isOpen={isOpen} setIsOpen={setIsOpen} onCreate={(id) => addTag(id)} />
        </>
    );
};
